/**
 * 통합관리자 — 일괄 청구
 * ---------------------------------------------------------------------------
 * ## 무엇을 없애려고 만들었나
 * 지금까지 청구서는 **기업 하나씩** 발행했다. 기업을 고르고 → 기간을 넣고 →
 * 금액을 확인하고 → 발행하고 → 닫고 → 다음 기업. 고객사가 열 곳만 되어도
 * 월초 하루가 이 일로 간다.
 *
 * 흔히 떠올리는 대안은 「담기 → 반복 → 일괄 실행」이다. 그런데 담는 행위
 * 자체가 기업 수만큼 반복된다 — 창만 안 열릴 뿐 같은 일이다.
 *
 * 그래서 **목록이 곧 장바구니**다. 청구할 수 있는 기업이 금액과 함께 이미
 * 올라와 있고, 운영자가 할 일은 <b>빼야 할 것만 체크를 끄는 것</b>이다.
 * 기업이 많을수록 이쪽이 빠르다.
 *
 * ## 금액을 목록에서 바로 보여 준다
 * 금액을 모르면 체크를 못 한다. 금액을 보려고 기업마다 창을 열어야 하면
 * 결국 하나씩 하는 일로 돌아간다. 서버가 기업마다 미리 계산해서 준다
 * (느리다 — 그래서 상한이 있다).
 *
 * ## 자동 기업도 같이 보여 준다
 * 수동만 보이면 "자동으로 둔 곳은 언제 얼마가 나가지?" 를 알 수 없다.
 * 한 목록에 두고 방식만 표시한다. 발행은 **고른 것만** 한다.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

export interface InvoiceCandidate {
  tenantId: string;
  companyName: string;
  slug: string;
  invoiceMode: 'AUTO' | 'MANUAL';
  periodStart: string;
  periodEnd: string;
  daysToPeriodEnd: number;
  dueDays: number;
  amount: number | null;
  creditAmount: number | null;
  error: string | null;
}

interface BatchResult {
  issued: number;
  skipped: number;
  failed: number;
  results: Array<{
    tenantId: string;
    companyName: string | null;
    ok: boolean;
    invoiceNo: string | null;
    totalAmount: number | null;
    alreadyExisted: boolean;
    error: string | null;
  }>;
}

const won = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : `${Math.round(v).toLocaleString('ko-KR')}원`;

interface AutomationPolicy {
  autoInvoiceEnabled: boolean;
  autoInvoiceLeadDays: number;
  suspendEnabled: boolean;
  suspendAfterDays: number;
  suspendMinAmount: number;
}

export function BatchInvoicePanel({ onOpenLedger }: { onOpenLedger: (tenantId: string) => void }) {
  const { message } = App.useApp();
  const client = useQueryClient();
  const [scope, setScope] = useState<'ALL' | 'MANUAL'>('ALL');
  const [picked, setPicked] = useState<string[]>([]);
  const [result, setResult] = useState<BatchResult | null>(null);

  /*
   * 「며칠 전에 나가는가」를 숫자로 적어 두지 않는다. 운영자가 공통코드에서
   * 바꿀 수 있는 값이라, 박아 두면 설정을 바꾼 순간 안내만 거짓말이 된다.
   */
  const policy = useQuery<AutomationPolicy, NormalizedError>({
    queryKey: ['admin', 'billing', 'automation-policy'],
    queryFn: async () => (await apiClient.get('/admin/billing/automation/policy')).data,
    staleTime: 300_000,
  });

  const candidates = useQuery<InvoiceCandidate[], NormalizedError>({
    queryKey: ['admin', 'billing', 'candidates', scope],
    queryFn: async () =>
      (
        await apiClient.get('/admin/billing/candidates', {
          params: { onlyManual: scope === 'MANUAL' ? 'true' : undefined, limit: 200 },
        })
      ).data,
    /*
     * 기업마다 금액을 계산하느라 느리다. 탭을 오갈 때마다 다시 돌리면
     * 운영자가 기다리기만 한다. 2분은 들고 있는다.
     */
    staleTime: 120_000,
  });

  const rows = candidates.data ?? [];

  /** 발행할 수 있는 줄만. 계산이 실패한 줄은 고를 수 없다 */
  const issuable = useMemo(() => rows.filter((r) => r.error === null), [rows]);

  const pickedRows = useMemo(
    () => issuable.filter((r) => picked.includes(r.tenantId)),
    [issuable, picked],
  );
  const pickedTotal = pickedRows.reduce((s, r) => s + (r.amount ?? 0), 0);

  const setMode = useMutation<unknown, NormalizedError, { tenantId: string; mode: 'AUTO' | 'MANUAL'; dueDays: number }>({
    mutationFn: async (v) =>
      (
        await apiClient.patch(`/admin/billing/preferences/${v.tenantId}`, {
          invoiceMode: v.mode,
          dueDays: v.dueDays,
        })
      ).data,
    onSuccess: () => {
      message.success('청구 방식을 바꿨습니다.');
      void client.invalidateQueries({ queryKey: ['admin', 'billing'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  const issueBatch = useMutation<BatchResult, NormalizedError>({
    mutationFn: async () =>
      (
        await apiClient.post('/admin/billing/invoices/batch', {
          targets: pickedRows.map((r) => ({
            tenantId: r.tenantId,
            periodStart: r.periodStart,
            periodEnd: r.periodEnd,
          })),
        })
      ).data,
    onSuccess: (r) => {
      setResult(r);
      setPicked([]);
      void client.invalidateQueries({ queryKey: ['admin', 'billing'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  const columns: ColumnsType<InvoiceCandidate> = [
    {
      title: '기업',
      key: 'company',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {/* 기업 이름을 누르면 그 기업의 거래 원장이 열린다 */}
          <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
            onClick={() => onOpenLedger(r.tenantId)}>
            {r.companyName}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.slug}</Text>
        </Space>
      ),
    },
    {
      title: '청구 방식',
      key: 'mode',
      width: 130,
      render: (_, r) => (
        <Tooltip
          title={
            r.invoiceMode === 'AUTO'
              ? '주기가 끝날 무렵 배치가 알아서 발행합니다.'
              : '배치가 건드리지 않습니다. 여기서 직접 보내세요.'
          }
        >
          <Switch
            size="small"
            checked={r.invoiceMode === 'AUTO'}
            checkedChildren="자동"
            unCheckedChildren="수동"
            loading={setMode.isPending && setMode.variables?.tenantId === r.tenantId}
            onChange={(on) =>
              setMode.mutate({
                tenantId: r.tenantId,
                mode: on ? 'AUTO' : 'MANUAL',
                dueDays: r.dueDays,
              })
            }
          />
        </Tooltip>
      ),
    },
    {
      title: '이용 기간',
      key: 'period',
      width: 210,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.periodStart} ~ {r.periodEnd}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.daysToPeriodEnd > 0
              ? `주기 종료까지 ${r.daysToPeriodEnd}일`
              : r.daysToPeriodEnd === 0
                ? '오늘 주기 종료'
                : `주기 종료 ${-r.daysToPeriodEnd}일 지남`}
            {' · '}납기 {r.dueDays}일
          </Text>
        </Space>
      ),
    },
    {
      title: '청구 금액',
      key: 'amount',
      width: 170,
      align: 'right',
      render: (_, r) =>
        r.error ? (
          <Tooltip title={r.error}>
            <Tag color="default">계산 못 함</Tag>
          </Tooltip>
        ) : (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <Text strong>{won(r.amount)}</Text>
            {(r.creditAmount ?? 0) > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                환불 차감 {won(r.creditAmount)} 포함
              </Text>
            )}
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="목록이 곧 장바구니입니다"
        description={
          <>
            청구할 수 있는 기업이 <b>금액과 함께</b> 이미 올라와 있습니다. 보낼 것만 체크하고
            아래 단추를 한 번 누르면 됩니다 — 기업을 하나씩 담을 필요가 없습니다.{' '}
            <b>자동</b>으로 둔 기업은 배치가 알아서 보내므로 보통 손댈 일이 없지만, 금액을
            미리 확인하실 수 있도록 함께 보여 드립니다.
            <br />
            {policy.data && (
              <>
                지금 설정: 자동 발행{' '}
                {policy.data.autoInvoiceEnabled ? (
                  <>
                    <b>켜짐</b> — 매일 새벽 2시에 돌면서, 청구 주기가 끝나기{' '}
                    <b>{policy.data.autoInvoiceLeadDays}일 전</b>인 기업의 청구서를 만들고
                    기업 관리자에게 안내 메일을 보냅니다.
                  </>
                ) : (
                  <>
                    <b>꺼짐</b> — 자동으로 두어도 배치가 돌지 않습니다. 모든 청구서를 여기서
                    직접 보내셔야 합니다.
                  </>
                )}
              </>
            )}
          </>
        }
      />

      <Space wrap align="center">
        <Segmented
          value={scope}
          onChange={(v) => { setScope(v as 'ALL' | 'MANUAL'); setPicked([]); }}
          options={[
            { value: 'ALL', label: '전체' },
            { value: 'MANUAL', label: '수동만' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void candidates.refetch()}>
          다시 계산
        </Button>
        <Text type="secondary">
          {rows.length}곳 · 고른 {pickedRows.length}곳 합계 <b>{won(pickedTotal)}</b>
        </Text>
      </Space>

      {candidates.isError && (
        <Alert type="error" showIcon message={errorText(candidates.error)} />
      )}

      <Table<InvoiceCandidate>
        rowKey="tenantId"
        size="small"
        loading={candidates.isLoading || candidates.isFetching}
        dataSource={rows}
        columns={columns}
        pagination={false}
        rowSelection={{
          selectedRowKeys: picked,
          onChange: (keys) => setPicked(keys as string[]),
          // 계산이 실패한 줄은 보낼 수 없다. 고를 수 있게 두면 눌러 놓고 실패한다.
          getCheckboxProps: (r) => ({ disabled: r.error !== null }),
        }}
        locale={{
          emptyText:
            '지금 청구할 기업이 없습니다. 이미 발행했거나, 주기가 아직 남았거나, 체험 중입니다.',
        }}
      />

      <Space>
        <Popconfirm
          title={`${pickedRows.length}곳에 청구서를 발행합니다`}
          description={
            <div style={{ maxWidth: 360 }}>
              합계 <b>{won(pickedTotal)}</b> 입니다. 발행하면 기업 관리자에게 안내 메일이
              나갑니다. 잘못 보냈다면 청구서를 무효화한 뒤 다시 발행하셔야 합니다.
            </div>
          }
          okText="발행"
          cancelText="취소"
          disabled={pickedRows.length === 0}
          onConfirm={() => issueBatch.mutate()}
        >
          <Button type="primary" disabled={pickedRows.length === 0} loading={issueBatch.isPending}>
            고른 {pickedRows.length}곳 일괄 발행
          </Button>
        </Popconfirm>
        {pickedRows.length === 0 && (
          <Text type="secondary">보낼 기업을 고르세요.</Text>
        )}
      </Space>

      {/* 결과는 건별로 보여 준다. 몇 건 되었다는 숫자만으로는 무엇이 실패했는지 모른다 */}
      <Modal
        open={result !== null}
        onCancel={() => setResult(null)}
        onOk={() => setResult(null)}
        title="일괄 발행 결과"
        width={720}
        footer={<Button type="primary" onClick={() => setResult(null)}>닫기</Button>}
      >
        {result && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={3} bordered>
              <Descriptions.Item label="발행">{result.issued}건</Descriptions.Item>
              <Descriptions.Item label="이미 있음">{result.skipped}건</Descriptions.Item>
              <Descriptions.Item label="실패">
                <Text type={result.failed > 0 ? 'danger' : undefined}>{result.failed}건</Text>
              </Descriptions.Item>
            </Descriptions>
            <Table
              rowKey="tenantId"
              size="small"
              pagination={false}
              dataSource={result.results}
              columns={[
                { title: '기업', dataIndex: 'companyName', key: 'c' },
                {
                  title: '결과',
                  key: 'r',
                  width: 110,
                  render: (_: unknown, r: BatchResult['results'][number]) =>
                    r.ok ? (
                      <Tag color={r.alreadyExisted ? 'default' : 'green'}>
                        {r.alreadyExisted ? '이미 있음' : '발행'}
                      </Tag>
                    ) : (
                      <Tag color="red">실패</Tag>
                    ),
                },
                { title: '청구서', dataIndex: 'invoiceNo', key: 'n', width: 170 },
                {
                  title: '금액',
                  key: 'a',
                  width: 120,
                  align: 'right',
                  render: (_: unknown, r: BatchResult['results'][number]) => won(r.totalAmount),
                },
                {
                  title: '사유',
                  dataIndex: 'error',
                  key: 'e',
                  render: (v: string | null) =>
                    v ? <Text type="danger" style={{ fontSize: 12 }}>{v}</Text> : null,
                },
              ]}
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              「이미 있음」은 그 기간 청구서가 이미 나가 있다는 뜻입니다 — 두 번 청구되지
              않았습니다. 실패한 곳은 사유를 고친 뒤 다시 고르시면 됩니다.
            </Paragraph>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
